import { MeetingProvider } from '@videosdk.live/react-sdk'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MeetingAppProvider } from '../MeetingAppContextDef'
import { MeetingContainer } from '../meeting/MeetingContainer'
import { LeaveScreen } from '../components/screens/LeaveScreen'
import { useAxios } from '@/contexts/AxiosProvider'
import { useUser } from '@clerk/clerk-react'
import { HallUsersProvider } from '@/contexts/HallUsersProvider'
import { HallProvider } from '@/contexts/HallProvider'
import { TurnsProvider } from '@/contexts/TurnsProvider'
import { JoiningScreen } from '@/components/screens/JoiningScreen'
import { useRouter } from '@/hooks/useRouter'
import { AzureTtsProvider } from '@/contexts/AzureTtsProvider'
import { FlowNotesProvider } from '@/contexts/FlowNotesProvider'
import { PollProvider } from '@/contexts/PollProvider'
import { MiscProvider } from '@/contexts/MiscProvider'
import AblyGlobalProvider from '@/contexts/AblyGlobalProvider'
import { defaultCriteria, Hall } from '@db8/shared'
import LoadingScreen from '@/components/screens/LoadingScreen'
import { ChatProvider } from '@/contexts/ChatProvider'

function DemoCreateMeeting() {
  const user = useUser()
  const { query } = useRouter()
  const created = JSON.parse(localStorage.getItem('created') || '{}')

  const [meetingId, setMeetingId] = useState('')
  const [participantName, setParticipantName] = useState('')
  const [token, setToken] = useState('')
  const [micOn, setMicOn] = useState(false)
  const [webcamOn, setWebcamOn] = useState(false)
  const [customAudioStream, setCustomAudioStream] = useState(null)
  const [customVideoStream, setCustomVideoStream] = useState(null)
  const [isMeetingStarted, setMeetingStarted] = useState(false)
  const [isMeetingLeft, setIsMeetingLeft] = useState(false)
  const [ownerId, setOwnerId] = useState(null)
  const [hall, setHall] = useState<Hall | null>(null)
  const [loaderMessages, setLoaderMessages] = useState([
    'Preparing the debate hall for you',
    'Almost there',
  ])
  const seededRef = useRef(false)

  const axios = useAxios()

  const navigate = useNavigate()

  const isMobile = window.matchMedia('only screen and (max-width: 768px)').matches

  useEffect(() => {
    if (isMobile) {
      window.onbeforeunload = () => {
        return 'Are you sure you want to exit?'
      }
    }
  }, [isMobile])

  useEffect(() => {
    if (created[query.id]) setMeetingId(query.id)
  }, [query.id])

  useEffect(() => {
    if (meetingId) {
      const fetchToken = async () => {
        try {
          const res = await axios.get(`/api/token/${meetingId}`)
          setToken(res.data.token)
          setOwnerId(res.data.ownerId)
        } catch (error) {
          navigate('/error')
        }
      }

      fetchToken()
    }
  }, [meetingId, navigate])

  useEffect(() => {
    if (user?.user && !seededRef.current) {
      setParticipantName(user.user.fullName ?? '')
      seededRef.current = true
    }
  }, [user?.user])

  useEffect(() => {
    const fetchHall = async () => {
      const { data } = await axios.get(`/api/hall/${meetingId}`)
      setHall({
        ...data,
        evaluationCriteria:
          data.evaluationCriteria.length > 0 ? data.evaluationCriteria : defaultCriteria,
      })
      setLoaderMessages([])
    }

    if (isMeetingStarted) fetchHall()
  }, [isMeetingStarted])

  return (
    <MeetingAppProvider>
      {isMeetingLeft ? (
        <LeaveScreen setIsMeetingLeft={setIsMeetingLeft} />
      ) : !isMeetingStarted ? (
        <JoiningScreen
          participantName={participantName}
          setParticipantName={setParticipantName}
          setMeetingId={setMeetingId}
          setToken={setToken}
          micOn={micOn}
          setMicOn={setMicOn}
          webcamOn={webcamOn}
          setWebcamOn={setWebcamOn}
          customAudioStream={customAudioStream}
          setCustomAudioStream={setCustomAudioStream}
          customVideoStream={customVideoStream}
          setCustomVideoStream={setCustomVideoStream}
          onClickStartMeeting={() => {
            setMeetingStarted(true)
          }}
          startMeeting={() => setMeetingStarted(true)}
          setIsMeetingLeft={setIsMeetingLeft}
        />
      ) : !token || !meetingId || !hall || loaderMessages?.length > 0 ? (
        <div className="w-screen min-h-[calc(var(--vh)*100)] bg-background">
          <LoadingScreen messages={loaderMessages} />
        </div>
      ) : (
        <MeetingProvider
          config={{
            meetingId,
            micEnabled: micOn,
            webcamEnabled: webcamOn,
            name: participantName || user?.user?.fullName,
            multiStream: true,
            customCameraVideoTrack: customVideoStream,
            customMicrophoneAudioTrack: customAudioStream,
            debugMode: true,
            metaData: {
              clerkUserId: user?.user?.id,
              imageUrl: user?.user?.imageUrl,
            },
          }}
          token={token}
          reinitialiseMeetingOnConfigChange={true}
          joinWithoutUserInteraction={true}
        >
          <AblyGlobalProvider meetingId={meetingId}>
            <ChatProvider channelName={`CHAT:${meetingId}`}>
              <HallProvider hall={hall} setHall={setHall} setLoaderMessages={setLoaderMessages}>
                <TurnsProvider>
                  <HallUsersProvider hallId={meetingId} ownerId={ownerId}>
                    <AzureTtsProvider>
                      <FlowNotesProvider>
                        <PollProvider>
                          <MiscProvider>
                            <MeetingContainer
                              onMeetingLeave={() => {}}
                              setIsMeetingLeft={setIsMeetingLeft}
                              isReady={meetingId && !!hall}
                            />
                          </MiscProvider>
                        </PollProvider>
                      </FlowNotesProvider>
                    </AzureTtsProvider>
                  </HallUsersProvider>
                </TurnsProvider>
              </HallProvider>
            </ChatProvider>
          </AblyGlobalProvider>
        </MeetingProvider>
      )}
    </MeetingAppProvider>
  )
}

export default DemoCreateMeeting
